import { Link } from "react-router-dom";
import { Clapperboard, ImageOff, ImagePlus, Lock } from "lucide-react";
import { useState } from "react";
import { API, libQs } from "@/lib/api";
import { Rating } from "@/components/TmdbDetails";
import { SelectMark } from "@/components/Selection";
import { RES_HINT, ratioLabel, resLabel } from "@/lib/videoFit";

export const thumbUrl = (id, v = 1) => `${API}/videos/${id}/thumb${libQs()}${libQs() ? "&" : "?"}v=${v}`;
export const posterUrl = (p) => (p ? `${API}/posters/image/${p.kind}/${encodeURIComponent(p.key)}${libQs()}${libQs() ? "&" : "?"}v=${p.version}` : null);
export const fmtClock = (s) => {
  if (!s) return "";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h ? `${h} h ${m} min` : `${m} min`;
};
export const epCode = (v) => (v.season != null && v.episode != null ? `S${String(v.season).padStart(2, "0")}E${String(v.episode).padStart(2, "0")}` : "");

/* 16:9 preview frame (lazy FFmpeg thumbnail) or, with `portrait`, a 2:3 poster card — TMDB/folder/uploaded poster when there is one,
   otherwise the thumbnail letterboxed over a blurred copy of itself. */
export const VideoThumb = ({ id, hasThumb = true, thumbV = 1, className = "", pct = 0, poster = null, portrait = false, children }) => {
  const [failed, setFailed] = useState(false);
  const [pFailed, setPFailed] = useState(false);
  const src = thumbUrl(id, thumbV);
  const showThumb = hasThumb !== false && !failed;
  const showPoster = portrait && poster && !pFailed;
  return (
    <div className={`relative w-full overflow-hidden rounded-lg bg-zinc-900 ${portrait ? "aspect-[2/3]" : "aspect-video"} ${className}`} data-poster={showPoster ? poster.origin : undefined}>
      {showPoster ? (
        <img src={posterUrl(poster)} alt="" loading="lazy" onError={() => setPFailed(true)} className="h-full w-full object-cover" />
      ) : portrait && showThumb ? (
        <>
          <img src={src} alt="" aria-hidden loading="lazy" className="absolute inset-0 h-full w-full scale-125 object-cover opacity-50 blur-xl" />
          <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} className="absolute inset-x-0 top-1/2 w-full -translate-y-1/2 object-cover shadow-lg" />
        </>
      ) : showThumb ? (
        <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-zinc-600">{failed ? <ImageOff size={22} /> : <Clapperboard size={22} />}</div>
      )}
      {pct > 0 && <div className="absolute inset-x-0 bottom-0 h-1 bg-black/40"><div className="h-full bg-amber-500" style={{ width: `${Math.min(100, pct)}%` }} /></div>}
      {children}
    </div>
  );
};

export const RatioBadge = ({ aspect, width, height, id, className = "", tone = "dark" }) => {
  const label = ratioLabel(aspect);
  if (!label) return null;
  const wide = aspect > 1.9, classic = aspect < 1.5;
  const res = resLabel(width, height);
  const low = res === "SD" || res === "Low";
  return (
    <span className={`flex items-center overflow-hidden rounded font-mono text-[10px] leading-none ${tone === "dark" ? "bg-black/70 text-white" : "border border-zinc-200 bg-white text-zinc-600"} ${className}`} data-testid={id ? `video-ratio-${id}` : undefined}>
      <span className="px-1.5 py-0.5" title={wide ? "Widescreen film — expect bars above and below on a 16:9 screen" : classic ? "Classic TV shape — expect bars left and right" : "Standard widescreen"}>{label}</span>
      {res && (
        <span className={`px-1.5 py-0.5 font-semibold ${low ? (tone === "dark" ? "bg-amber-400/90 text-zinc-900" : "bg-amber-100 text-amber-900") : tone === "dark" ? "bg-white/15" : "bg-zinc-100 text-zinc-700"}`}
          title={`${width}×${height} — ${RES_HINT[res]}${low ? ". Low-quality copy" : ""}`} data-testid={id ? `video-res-${id}` : undefined}>{res}</span>
      )}
    </span>
  );
};

export const UnplayableBadge = ({ ext, className = "" }) => (
  <span title="Browsers can only play MP4, M4V, MOV and WebM" className={`inline-flex items-center gap-1 rounded-full bg-zinc-800/90 px-2 py-0.5 text-[10px] font-semibold uppercase text-zinc-200 ${className}`} data-testid="video-unplayable">
    <Lock size={10} />{ext} · not playable
  </span>
);

/* Album-style card for one video: 16:9 preview (episodes) or 2:3 poster (movies), then title · duration · year · file type. */
export const VideoCard = ({ v, portrait = false, onPoster, sel }) => {
  const pr = v.progress || {};
  const on = sel?.has(v.id);
  return (
    <Link to={`/videos/watch/${v.id}`} data-testid={`video-card-${v.id}`} data-sel-id={v.id} aria-pressed={sel?.mode ? !!on : undefined}
      onClick={(e) => { if (sel?.mode) { e.preventDefault(); sel.toggle(v.id, e.shiftKey); } }}
      className={`group flex flex-col rounded-xl p-2 transition-colors hover:bg-white/60 ${on ? "bg-white ring-2 ring-zinc-900" : ""}`}>
      <VideoThumb id={v.id} hasThumb={v.has_thumb} thumbV={v.thumb_v} pct={pr.finished ? 100 : pr.pct} poster={v.poster} portrait={portrait} className={`shadow-md transition-transform duration-300 ${sel?.mode ? (on ? "scale-[0.96]" : "") : "group-hover:-translate-y-1"}`}>
        {sel?.mode && <SelectMark on={on} className="bottom-2 left-2" />}
        {epCode(v) && <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 font-mono text-[10px] font-semibold text-white backdrop-blur">{epCode(v)}</span>}
        {pr.finished ? <span className="absolute right-2 top-2 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white">Watched</span>
          : pr.pct > 0 ? <span className="absolute right-2 top-2 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-semibold text-white">{Math.round(pr.pct)}%</span> : null}
        {!v.playable && !sel?.mode && <UnplayableBadge ext={v.ext} className="absolute bottom-2 left-2" />}
        {v.playable && v.aspect && !sel?.mode ? <RatioBadge aspect={v.aspect} width={v.width} height={v.height} className="absolute bottom-2 right-2" id={v.id} /> : null}
        {onPoster && (
          <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPoster(v); }} title="Change poster" data-testid={`video-card-poster-${v.id}`}
            className="absolute bottom-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white opacity-0 backdrop-blur transition-opacity hover:bg-black/80 group-hover:opacity-100 focus:opacity-100">
            <ImagePlus size={13} />
          </button>
        )}
      </VideoThumb>
      <p className="mt-2 truncate text-sm font-medium text-zinc-900" title={v.title}>{v.title}</p>
      <p className="flex items-center gap-1.5 truncate text-xs text-zinc-500"><Rating value={v.tmdb?.rating || v.episode_info?.rating} /><span className="truncate">{[v.duration ? fmtClock(v.duration) : null, v.tmdb?.year || v.year, v.ext.toUpperCase()].filter(Boolean).join(" · ")}</span></p>
    </Link>
  );
};

/* One row in an episode / collection list. */
export const VideoRow = ({ v, active = false }) => {
  const pr = v.progress || {};
  return (
    <li>
      <Link to={`/videos/watch/${v.id}`} data-testid={`video-row-${v.id}`} aria-current={active ? "true" : undefined}
        className={`group flex items-center gap-3 rounded-xl p-2 transition-colors hover:bg-white/70 ${active ? "bg-white/80" : ""}`}>
        <VideoThumb id={v.id} hasThumb={v.has_thumb} thumbV={v.thumb_v} pct={pr.finished ? 100 : pr.pct} className="w-36 shrink-0 sm:w-44" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-zinc-900">{epCode(v) && <span className="mr-2 font-mono text-xs text-zinc-400">{epCode(v)}</span>}{v.title}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
            {v.duration ? <span>{fmtClock(v.duration)}</span> : null}
            {v.year ? <span>{v.year}</span> : null}
            <span className="uppercase">{v.ext}</span>
            {pr.finished ? <span className="text-emerald-600">Watched</span> : pr.pct > 0 ? <span className="text-amber-600">{Math.round(pr.pct)}%</span> : null}
            {!v.playable && <UnplayableBadge ext={v.ext} />}
            {v.playable && v.aspect ? <RatioBadge aspect={v.aspect} width={v.width} height={v.height} id={v.id} tone="light" /> : null}
          </p>
        </div>
      </Link>
    </li>
  );
};
